import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Table,
  Input,
  Button,
  Tag,
  message,
  Row,
  Col,
  Select,
  Typography,
  Empty,
} from "antd";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { notificacionAction } from "../actions/notificationActions";
import Notification from "../utils/api/notifications";
import MainLayout from "../layouts/Main";
import useAppStore from "../store/useAppStore";
import "../scss/notifications.scss";

const { Title } = Typography;
const { Option } = Select;

export default function AllNotifications() {
  const storeNotifications = useAppStore((state) => state.notifications);
  const markAsRead = useAppStore((state) => state.markNotificationAsRead);
  const markAllAsReadStore = useAppStore(
    (state) => state.markAllNotificationsAsRead
  );

  const [notifications, setNotifications] = useState([]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const location = useLocation();
  const navigate = useNavigate();
  const role = localStorage.getItem("role");

  let userId;
  try {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      const user = JSON.parse(userStr);
      userId = user?.id;
    }
  } catch (error) {
    console.error("Error parsing user from localStorage:", error);
  }

  const [load, setLoad] = useState(true);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [apiError, setApiError] = useState(false);
  const stateNotificaciones = useSelector(
    (state) => state.notifications.stateNotificacionesTotal
  );
  const dispatch = useDispatch();

  const selectedNotificationId = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("selected");
  }, [location.search]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const notificacionesChange = (state) => {
    dispatch(notificacionAction(state));
  };

  // ========== FUNCIONES DE NAVEGACIÓN (una sola vez) ==========
  const normalizeNotificationRoute = useCallback((rawRoute) => {
    if (!rawRoute || typeof rawRoute !== "string") {
      return null;
    }

    const trimmedRoute = rawRoute.trim();
    if (!trimmedRoute) {
      return null;
    }

    if (trimmedRoute.startsWith("/#/")) {
      return trimmedRoute.replace("/#", "");
    }

    if (trimmedRoute.startsWith("#/")) {
      return trimmedRoute.replace("#", "");
    }

    if (trimmedRoute.startsWith("/")) {
      return trimmedRoute;
    }

    return null;
  }, []);

  const getNotificationOrderReference = useCallback((notification) => {
    if (!notification || typeof notification !== "object") {
      return "";
    }

    const directReference = [
      notification.orderId,
      notification.order_id,
      notification.related_id,
      notification.reference_id,
      notification.orderNum,
      notification.order_num,
    ].find(
      (value) =>
        value !== null && value !== undefined && String(value).trim() !== ""
    );

    if (directReference !== undefined) {
      return String(directReference).trim();
    }

    const searchableText = `${notification.title || ""} ${
      notification.description || ""
    }`;
    const orderCodeMatch = searchableText.match(/(?:#)?((?:MKT|REQ)-\d+)/i);

    return orderCodeMatch?.[1] || "";
  }, []);

  const getMarketplaceOrderId = useCallback((notification) => {
    if (!notification || typeof notification !== "object") {
      return "";
    }

    const idCandidates = [
      notification.marketplaceOrderId,
      notification.marketplace_order_id,
      notification.orderMarketplaceId,
      notification.order_marketplace_id,
      notification.marketplace_order,
      notification.order?.marketplace_order_id,
      notification.order?.id,
      notification.orderId,
      notification.order_id,
      notification.related_id,
      notification.reference_id,
    ];

    for (const candidate of idCandidates) {
      if (candidate === null || candidate === undefined) {
        continue;
      }

      const normalized = String(candidate).trim();
      if (!normalized) {
        continue;
      }

      const upperCandidate = normalized.toUpperCase();
      if (upperCandidate.startsWith("REQ-")) {
        continue;
      }

      return normalized;
    }

    const searchableText = `${notification.title || ""} ${
      notification.description || ""
    }`;
    const marketplaceCodeMatch = searchableText.match(/(?:#)?(MKT-\d+)/i);
    if (marketplaceCodeMatch?.[1]) {
      return marketplaceCodeMatch[1].toUpperCase();
    }

    return "";
  }, []);

  const getNotificationProductTarget = useCallback((notification) => {
    if (!notification || typeof notification !== "object") {
      return null;
    }

    const searchableText = `${notification.title || ""} ${
      notification.description || ""
    }`.toLowerCase();
    const hasProductKeyword =
      searchableText.includes("producto") || searchableText.includes("product");

    const explicitProductSignal = [
      notification.productId,
      notification.product_id,
      notification.marketplaceProductId,
      notification.marketplace_product_id,
      notification.product?.id,
      notification.product_name,
      notification.productName,
    ].some(
      (value) =>
        value !== null && value !== undefined && String(value).trim() !== ""
    );

    if (!hasProductKeyword && !explicitProductSignal) {
      return null;
    }

    const productIdCandidates = [
      notification.productId,
      notification.product_id,
      notification.marketplaceProductId,
      notification.marketplace_product_id,
      notification.product?.id,
      notification.item?.id,
      notification.item_id,
      notification.related_product_id,
      notification.reference_product_id,
    ];

    let productId = "";
    for (const candidate of productIdCandidates) {
      if (candidate === null || candidate === undefined) {
        continue;
      }

      const normalized = String(candidate).trim();
      if (!normalized) {
        continue;
      }

      const upperValue = normalized.toUpperCase();
      if (upperValue.startsWith("MKT-") || upperValue.startsWith("REQ-")) {
        continue;
      }

      productId = normalized;
      break;
    }

    const productNameCandidates = [
      notification.product_name,
      notification.productName,
      notification.product?.name,
      notification.name,
    ];

    let productName = "";
    for (const candidate of productNameCandidates) {
      if (candidate === null || candidate === undefined) {
        continue;
      }

      const normalized = String(candidate).trim();
      if (normalized) {
        productName = normalized;
        break;
      }
    }

    if (!productName) {
      const fullText = `${notification.title || ""} ${
        notification.description || ""
      }`;
      const extractedNameMatch = fullText.match(
        /agreg[oó]\s+el\s+producto\s+([^.!]+)/i
      );
      if (extractedNameMatch?.[1]) {
        productName = extractedNameMatch[1].trim();
      }
    }

    if (!productId) {
      if (productName) {
        return `/marketplace?search=${encodeURIComponent(productName)}`;
      }

      return "/marketplace";
    }

    const basePath = `/marketplace/producto/detalle/${encodeURIComponent(
      productId
    )}`;
    if (productName) {
      return `${basePath}?name=${encodeURIComponent(productName)}`;
    }

    return basePath;
  }, []);

  const extractRegisteredUserName = useCallback((notification) => {
    if (!notification || typeof notification !== "object") {
      return "";
    }

    const directName = [
      notification.user_name,
      notification.userName,
      notification.client_name,
      notification.customer_name,
      notification.user?.name,
      notification.name,
    ].find(
      (value) =>
        value !== null && value !== undefined && String(value).trim() !== ""
    );

    if (directName !== undefined) {
      return String(directName).trim();
    }

    const sanitizeExtractedName = (rawName) => {
      if (!rawName) {
        return "";
      }

      const cleanName = String(rawName)
        .replace(/\s+/g, " ")
        .replace(/[.,;:!?]+$/g, "")
        .trim();

      if (!cleanName) {
        return "";
      }

      const lowered = cleanName.toLowerCase();
      if (
        lowered === "nuevo" ||
        lowered === "nueva" ||
        lowered === "nuevo usuario" ||
        lowered === "nuevo cliente" ||
        lowered === "nuevo proveedor"
      ) {
        return "";
      }

      return cleanName;
    };

    const searchableText = `${notification.title || ""} ${
      notification.description || ""
    }`;
    const welcomeMatch = searchableText.match(/bienvenida\s+a\s+([^.!]+)/i);
    const welcomeName = sanitizeExtractedName(welcomeMatch?.[1]);
    if (welcomeName) {
      return welcomeName;
    }

    const userRegisteredMatch = searchableText.match(
      /(?:usuario|cliente|proveedor)\s+(?!nuevo\b|nueva\b)([A-Za-zÀ-ÿ'’\-\s]{3,})\s+se\s+ha\s+registrado/i
    );
    const registeredName = sanitizeExtractedName(userRegisteredMatch?.[1]);
    if (registeredName) {
      return registeredName;
    }

    const accountOwnerMatch = searchableText.match(
      /cuenta\s+del\s+(?:proveedor|cliente|usuario)\s+([A-Za-zÀ-ÿ'’\-\s]{3,})/i
    );
    const accountOwnerName = sanitizeExtractedName(accountOwnerMatch?.[1]);
    if (accountOwnerName) {
      return accountOwnerName;
    }

    return "";
  }, []);

  const getAdminUsersNotificationTarget = useCallback(
    (notification) => {
      if (role !== "1" || !notification || typeof notification !== "object") {
        return null;
      }

      const searchableText = `${notification.title || ""} ${
        notification.description || ""
      }`.toLowerCase();
      const hasUserKeywords =
        searchableText.includes("cliente") ||
        searchableText.includes("usuario") ||
        searchableText.includes("proveedor") ||
        searchableText.includes("bienvenida");
      const hasRegistrationKeywords =
        searchableText.includes("registrad") ||
        searchableText.includes("bienvenida");
      const isOrderRegistration =
        searchableText.includes("orden") &&
        searchableText.includes("registrad");
      const isNewUserNotification =
        hasUserKeywords && hasRegistrationKeywords && !isOrderRegistration;

      if (!isNewUserNotification) {
        return null;
      }

      const userName = extractRegisteredUserName(notification);
      return userName ? `/@u?search=${encodeURIComponent(userName)}` : "/@u";
    },
    [extractRegisteredUserName, role]
  );

  const resolveNotificationTarget = useCallback(
    (notification) => {
      const directRoute = normalizeNotificationRoute(
        notification?.route ||
          notification?.path ||
          notification?.url ||
          notification?.link
      );

      if (directRoute) {
        return directRoute;
      }

      const adminUsersTarget = getAdminUsersNotificationTarget(notification);
      if (adminUsersTarget) {
        return adminUsersTarget;
      }

      const productTarget = getNotificationProductTarget(notification);
      if (productTarget) {
        return productTarget;
      }

      const orderReference = getNotificationOrderReference(notification);
      const normalizedOrderReference = orderReference.toUpperCase();
      const searchableText = `${notification?.title || ""} ${
        notification?.description || ""
      }`;
      const hasMarketplaceCode =
        normalizedOrderReference.startsWith("MKT-") ||
        /(?:^|\s)#?MKT-\d+/i.test(searchableText);

      if (hasMarketplaceCode) {
        const marketplaceOrderId = getMarketplaceOrderId(notification);
        if (marketplaceOrderId) {
          return `/marketplace/orders/details/${encodeURIComponent(
            marketplaceOrderId
          )}`;
        }

        return "/marketplace/orders";
      }

      if (orderReference) {
        return `/orders/details/new/${encodeURIComponent(orderReference)}`;
      }

      const selectedId = notification?.id;
      return selectedId ? `/@a-@n?selected=${selectedId}` : "/@a-@n";
    },
    [
      getAdminUsersNotificationTarget,
      getMarketplaceOrderId,
      getNotificationOrderReference,
      getNotificationProductTarget,
      normalizeNotificationRoute,
    ]
  );

  useEffect(() => {
    if (!userId) {
      setLoad(false);
      setApiError(true);
      return;
    }

    if (storeNotifications && storeNotifications.length > 0) {
      setNotifications(storeNotifications);
      setLoad(false);
      setApiError(false);
      return;
    }

    setApiError(false);
    Notification.GetNotifications(userId)
      .then((response) => {
        const fetchedNotifications = Array.isArray(response.data)
          ? response.data
          : [];
        setNotifications(fetchedNotifications);
        setLoad(false);
      })
      .catch((error) => {
        message.error("Error cargando notificaciones");
        setNotifications([]);
        setApiError(true);
        setLoad(false);
      });
  }, [reload, userId, stateNotificaciones, storeNotifications]);

  const filteredNotifications = useMemo(() => {
    if (!Array.isArray(notifications)) {
      return [];
    }

    return notifications.filter((notification) => {
      if (statusFilter !== "all") {
        const statusValue = statusFilter === "read" ? true : false;
        if (notification.seen !== statusValue) return false;
      }

      if (searchText) {
        const searchLower = searchText.toLowerCase();
        const title = (notification.title || "").toLowerCase();
        const description = (notification.description || "").toLowerCase();
        const orderId = (
          notification.orderId ||
          notification.order_id ||
          notification.related_id ||
          notification.reference_id ||
          ""
        )
          .toString()
          .toLowerCase();

        if (
          !title.includes(searchLower) &&
          !description.includes(searchLower) &&
          !orderId.includes(searchLower)
        ) {
          return false;
        }
      }

      return true;
    });
  }, [notifications, statusFilter, searchText]);

  const handleNotificationOpen = useCallback(
    (notification) => {
      if (!notification || typeof notification !== "object") {
        return;
      }

      const target = resolveNotificationTarget(notification);
      if (notification.id && !notification.seen) {
        markAsRead(notification.id);
      }
      navigate(target);
    },
    [markAsRead, navigate, resolveNotificationTarget]
  );

  const tableData = useMemo(() => {
    if (!Array.isArray(filteredNotifications)) {
      return [];
    }

    return filteredNotifications.map((ntf) => {
      var status = ntf.seen;
      var ntfS;
      if (status == 0 || status === false) {
        ntfS = "No vista";
      } else if (status == 1 || status === true) {
        ntfS = "Abierta";
      }

      const description =
        ntf.description && ntf.description !== "DESCRIPCION SIN DECLARAR"
          ? ntf.description
          : "Notificación sin descripción";

      const title =
        ntf.title && ntf.title !== "SIN TITULO DE BACK"
          ? ntf.title
          : "Sin título";

      const rawDate = ntf.createdAt || ntf.created_at;
      const momentDate = moment(rawDate);
      const formattedDate = momentDate.isValid()
        ? momentDate.format("L")
        : "Fecha no válida";

      return {
        key: ntf.id || Math.random().toString(),
        notify: description,
        order: title,
        date: formattedDate,
        rawDate: rawDate,
        momentDate: momentDate.isValid() ? momentDate : moment(),
        status: ntfS,
        details: (
          <Button
            type="link"
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              handleNotificationOpen(ntf);
            }}
            className="details-link"
          >
            Ver detalle
          </Button>
        ),
        rawStatus: ntfS,
        id: ntf.id,
        notification: ntf,
      };
    });
  }, [filteredNotifications, handleNotificationOpen]);

  useEffect(() => {
    if (
      !selectedNotificationId ||
      !Array.isArray(tableData) ||
      tableData.length === 0
    )
      return;

    const selectedIndex = tableData.findIndex(
      (row) => String(row.key) === String(selectedNotificationId)
    );

    if (selectedIndex >= 0) {
      const selectedKey = tableData[selectedIndex].key;
      setSelectedRowKeys([selectedKey]);
      setCurrentPage(Math.floor(selectedIndex / pageSize) + 1);
    }
  }, [selectedNotificationId, tableData, pageSize]);

  const markRead = () => {
    if (!Array.isArray(selectedRowKeys) || selectedRowKeys.length === 0) {
      message.warning("Selecciona al menos una notificación");
      return;
    }

    setLoading(true);

    selectedRowKeys.forEach((id) => {
      markAsRead(id);
    });

    Notification.MarkNotificationsAsRead(selectedRowKeys)
      .then((response) => {
        const isSuccess = response?.status >= 200 && response?.status < 300;
        const payloadSuccess = response?.data?.success;

        if (
          isSuccess ||
          payloadSuccess === true ||
          payloadSuccess === undefined
        ) {
          message.success("Información actualizada correctamente.");
          notificacionesChange(!stateNotificaciones);
          setSelectedRowKeys([]);
        } else {
          message.error(
            "No se pudieron marcar las notificaciones como leídas."
          );
        }
        setLoading(false);
      })
      .catch((error) => {
        message.error("Error al actualizar la información.");
        setLoading(false);
      });
  };

  const getTableScrollX = () => {
    if (windowWidth <= 480) {
      return 900;
    } else if (windowWidth <= 768) {
      return 1100;
    }
    return "max-content";
  };

  const getScrollHeight = () => {
    if (windowWidth <= 480) {
      return "calc(100vh - 420px)";
    } else if (windowWidth <= 768) {
      return "calc(100vh - 380px)";
    }
    return "calc(100vh - 350px)";
  };

  const getColumns = () => {
    const baseColumns = [
      {
        title: "Notificación",
        dataIndex: "notify",
        key: "notify",
        width: windowWidth <= 480 ? 250 : windowWidth <= 768 ? 300 : "35%",
        className: "column-notify",
        ellipsis: {
          showTitle: false,
        },
        render: (text) => (
          <div className="notification-cell" title={text}>
            {text}
          </div>
        ),
      },
      {
        title: "Orden",
        dataIndex: "order",
        key: "order",
        width: windowWidth <= 480 ? 200 : windowWidth <= 768 ? 250 : "25%",
        className: "column-order",
        ellipsis: {
          showTitle: false,
        },
        render: (text) => (
          <div className="order-cell" title={text}>
            {text}
          </div>
        ),
      },
      {
        title: "Fecha",
        dataIndex: "date",
        key: "date",
        width: windowWidth <= 480 ? 120 : windowWidth <= 768 ? 130 : "15%",
        className: "column-date",
        sorter: (a, b) => {
          if (a.momentDate.isBefore(b.momentDate)) {
            return -1;
          }
          if (a.momentDate.isAfter(b.momentDate)) {
            return 1;
          }
          return 0;
        },
        sortDirections: ["descend", "ascend"],
        defaultSortOrder: "descend",
      },
      {
        title: "Estado",
        dataIndex: "status",
        key: "status",
        width: windowWidth <= 480 ? 100 : windowWidth <= 768 ? 110 : "12%",
        className: "column-status",
        render: (status) => {
          let color;
          if (status === "No vista") {
            color = "default";
          } else if (status === "Abierta") {
            color = "green";
          }
          return (
            <Tag color={color} className="status-tag">
              {status}
            </Tag>
          );
        },
      },
      {
        title: "Detalles",
        dataIndex: "details",
        key: "details",
        width: windowWidth <= 480 ? 80 : windowWidth <= 768 ? 90 : "13%",
        className: "column-details",
      },
    ];

    return baseColumns;
  };

  const onSelectChange = (selectedRowKeys) => {
    setSelectedRowKeys(selectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    columnWidth: windowWidth <= 480 ? 40 : 48,
    fixed: true,
  };

  const hasSelected =
    Array.isArray(selectedRowKeys) && selectedRowKeys.length > 0;

  const handleFilterChange = (value) => {
    setSelectedRowKeys([]);
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const onSearchChange = (e) => {
    setSelectedRowKeys([]);
    setSearchText(e.target.value);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchText("");
    setStatusFilter("all");
  };

  const emptyTableConfig = {
    emptyText: () => (
      <div className="empty-table-container">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span className="empty-description">
              {apiError
                ? "Error al cargar las notificaciones"
                : searchText || statusFilter !== "all"
                ? "No se encontraron notificaciones con los filtros aplicados"
                : "No tienes notificaciones"}
            </span>
          }
        >
          {apiError ? (
            <Button
              type="primary"
              onClick={() => {
                setReload(!reload);
                setApiError(false);
              }}
              className="empty-button"
            >
              Reintentar
            </Button>
          ) : (
            (searchText || statusFilter !== "all") && (
              <Button
                type="primary"
                onClick={clearFilters}
                className="empty-button"
              >
                Limpiar filtros
              </Button>
            )
          )}
        </Empty>
      </div>
    ),
  };

  const unreadCount = useMemo(() => {
    return notifications.filter((n) => !n.seen).length;
  }, [notifications]);

  const renderPagination = () => {
    if (
      !Array.isArray(filteredNotifications) ||
      filteredNotifications.length === 0
    ) {
      return false;
    }

    const totalPages = Math.ceil(filteredNotifications.length / pageSize);

    if (windowWidth <= 576) {
      return {
        current: currentPage,
        pageSize: pageSize,
        total: filteredNotifications.length,
        showSizeChanger: false,
        showQuickJumper: false,
        showTotal: false,
        onChange: (page, size) => {
          setCurrentPage(page);
          setPageSize(size);
        },
        position: ["bottomCenter"],
        size: "small",
        itemRender: (page, type, originalElement) => {
          if (type === "prev") {
            return (
              <Button
                size="small"
                disabled={currentPage === 1}
                className="mobile-pagination-btn"
              >
                Anterior
              </Button>
            );
          }
          if (type === "next") {
            return (
              <Button
                size="small"
                disabled={currentPage === totalPages}
                className="mobile-pagination-btn"
              >
                Siguiente
              </Button>
            );
          }
          if (type === "page") {
            const isActive = currentPage === page;
            return (
              <Button
                size="small"
                type={isActive ? "primary" : "default"}
                className={`mobile-pagination-page ${isActive ? "active" : ""}`}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </Button>
            );
          }
          return originalElement;
        },
      };
    }

    return {
      current: currentPage,
      pageSize: pageSize,
      total: filteredNotifications.length,
      pageSizeOptions:
        windowWidth <= 768
          ? ["10", "15", "20"]
          : ["10", "15", "20", "50", "100"],
      showSizeChanger: true,
      onChange: (page, size) => {
        setCurrentPage(page);
        setPageSize(size);
      },
      showTotal:
        windowWidth <= 768
          ? null
          : (total, range) =>
              `${range[0]}-${range[1]} de ${total} notificaciones`,
      showQuickJumper: false,
      position: ["bottomCenter"],
      size: windowWidth <= 768 ? "small" : "default",
    };
  };

  return (
    <MainLayout>
      <div className="orders-card">
        <Row gutter={[24, 16]}>
          <Col xs={24}>
            <div className="notifications-header">
              <Title level={windowWidth <= 480 ? 4 : 3}>Notificaciones</Title>
            </div>
          </Col>

          <Col xs={24} sm={24} md={8} lg={6}>
            <Select
              value={statusFilter}
              style={{ width: "100%" }}
              onChange={handleFilterChange}
              placeholder="Filtrar por estado"
              disabled={apiError}
              className="filter-select"
              size={windowWidth <= 480 ? "middle" : "large"}
            >
              <Option value="all">Todos los Estados</Option>
              <Option value="unread">No vistas ({unreadCount})</Option>
              <Option value="read">Leídas</Option>
            </Select>
          </Col>

          <Col xs={24} sm={24} md={16} lg={12}>
            <Input
              placeholder="Buscar en notificaciones..."
              value={searchText}
              onChange={onSearchChange}
              style={{ width: "100%" }}
              allowClear
              disabled={apiError}
              className="search-input"
              size={windowWidth <= 480 ? "middle" : "large"}
            />
          </Col>

          <Col xs={24} sm={24} md={8} lg={6}>
            <div
              style={{ display: "flex", alignItems: "center", height: "100%" }}
            >
              <Button
                type="primary"
                onClick={markRead}
                disabled={!hasSelected || apiError}
                loading={loading}
                style={{ width: "100%" }}
                className="mark-read-button"
                size={windowWidth <= 480 ? "middle" : "large"}
              >
                {windowWidth <= 480
                  ? `Leer (${selectedRowKeys.length})`
                  : `Marcar como leído (${selectedRowKeys.length})`}
              </Button>
            </div>
          </Col>

          {hasSelected &&
            Array.isArray(filteredNotifications) &&
            filteredNotifications.length > 0 && (
              <Col xs={24}>
                <div className="selection-indicator">
                  <span className="selected-count">
                    {selectedRowKeys.length} notificación
                    {selectedRowKeys.length !== 1 ? "es" : ""} seleccionada
                    {selectedRowKeys.length !== 1 ? "s" : ""}
                  </span>
                </div>
              </Col>
            )}

          <Col xs={24}>
            <div className="table-wrapper">
              <Table
                columns={getColumns()}
                dataSource={tableData}
                scroll={{
                  x: getTableScrollX(),
                  y: getScrollHeight(),
                }}
                rowClassName={(record) => {
                  const classes = ["notifications-table-row"];
                  if (record?.rawStatus === "No vista") {
                    classes.push("table-row-unread");
                  }
                  if (
                    selectedNotificationId &&
                    String(record?.key) === String(selectedNotificationId)
                  ) {
                    classes.push("table-row-selected");
                  }
                  return classes.join(" ");
                }}
                rowSelection={rowSelection}
                onRow={(record) => ({
                  onClick: (event) => {
                    const clickedSelectionColumn = event.target.closest(
                      "td.ant-table-selection-column"
                    );
                    if (clickedSelectionColumn) {
                      return;
                    }

                    const notification = record?.notification;
                    if (!notification) {
                      return;
                    }
                    handleNotificationOpen(notification);
                  },
                })}
                loading={load}
                className="styled-table notifications-table"
                locale={emptyTableConfig}
                pagination={renderPagination()}
                size={windowWidth <= 576 ? "small" : "middle"}
                tableLayout="fixed"
              />
            </div>
          </Col>
        </Row>
      </div>
    </MainLayout>
  );
}
